import { area, boxCorners, distance, formatArea, formatDistance, pathLength, type LatLng } from './geo'

/** The TOC's drawing tools: what each is, how many points it takes, and its running answer. */
export type DrawTool = 'marker' | 'route' | 'area' | 'box' | 'ring' | 'measure'

export const TOOLS: { tool: DrawTool; label: string; hint: string; icon: string }[] = [
  { tool: 'marker', label: 'Marker', hint: 'Click where it goes', icon: 'M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5' },
  { tool: 'route', label: 'Route', hint: 'Click points along it', icon: 'M4 19l5-6 4 3 7-10M4 19h0M20 6h0' },
  { tool: 'area', label: 'Area', hint: 'Click its corners', icon: 'M12 3l8 6-3 11H7L4 9z' },
  { tool: 'box', label: 'Box', hint: 'Two opposite corners', icon: 'M5 5h14v14H5z' },
  { tool: 'ring', label: 'Range ring', hint: 'Centre, then edge', icon: 'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM12 12h0' },
  { tool: 'measure', label: 'Measure', hint: 'Click to measure', icon: 'M3 17l14-14 4 4L7 21zM7 13l2 2M10 10l2 2M13 7l2 2' },
]

/** How many points each tool takes before it stops collecting them. */
export const VERTEX_LIMIT: Partial<Record<DrawTool, number>> = { marker: 1, box: 2, ring: 2 }

export function isComplete(tool: DrawTool, points: LatLng[]): boolean {
  switch (tool) {
    case 'marker':
      return points.length === 1
    case 'route':
      return points.length >= 2
    case 'area':
      return points.length >= 3
    case 'box':
    case 'ring':
      return points.length === 2
    case 'measure':
      return false
  }
}

/** The running answer: how far, how big. */
export function summary(tool: DrawTool, points: LatLng[]): string {
  const hint = TOOLS.find((entry) => entry.tool === tool)!.hint
  if (tool === 'ring') {
    return points.length < 2 ? hint : `${formatDistance(distance(points[0], points[1]))} radius`
  }
  if (tool === 'box') {
    if (points.length < 2) return hint
    return formatArea(area(boxCorners(points[0], points[1])))
  }
  if (tool === 'area') {
    if (points.length < 3) return hint
    return `${formatArea(area(points))} · ${formatDistance(pathLength([...points, points[0]]))} around`
  }
  if (points.length < 2) return hint
  return formatDistance(pathLength(points))
}
